import React, { memo, useCallback, useId, useRef, useState } from "react";
import clsx from "clsx";
import styles from "./Selectlist.module.scss";
import Icon from "../atoms/Icon";
import { useCloseOnOutsideClick } from "../../../../../../common-deprecated/hooks";

export type Option = {
    id: number;
    label: string;
    selected: boolean;
    onClick: () => void;
};

const SelectList = ({
    label,
    placeholder,
    options,
}: {
    label: string;
    placeholder: string;
    options: Option[];
}): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState<boolean>();
    const [searchQuery, setSearchQuery] = useState<string>();

    const uniqueId = useId();

    const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    }, []);

    const handleOptionClick = useCallback((onClick: () => void): void => {
        setOpen(false);
        onClick();
    }, []);

    const filteredOptions = options.filter((option) => {
        if (searchQuery) {
            return option.label.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return true;
    });

    useCloseOnOutsideClick(containerRef, () => setOpen(false));

    return (
        <div className={styles.select} ref={containerRef}>
            <button
                aria-label={label}
                aria-haspopup="true"
                aria-controls={uniqueId}
                aria-expanded={isOpen}
                onClick={() => setOpen(!isOpen)}
                type="button"
                className={clsx(styles.button, isOpen && styles.open)}
            >
                <span>{label}</span>
                <Icon variant="chevron-down" sizing="xs" />
            </button>
            {isOpen && (
                <div className={styles.listbox}>
                    <div className={styles.search}>
                        <input
                            type="search"
                            placeholder={placeholder}
                            name={label}
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                    </div>
                    <ul id={uniqueId} role="listbox" tabIndex={-1}>
                        {filteredOptions.map((option) => (
                            <li role="option" aria-selected={option.selected} key={option.id}>
                                <button type="button" onClick={() => handleOptionClick(option.onClick)}>
                                    {option.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default memo(SelectList);
